import getWatchedProgress from "../video/utils/getWatchedProgress";
import {isCompleted} from "../video/utils/getProgressData";

// Which orientation videos does this person still owe? (Marco 08-25)
//
// Program family decides WHICH video: SNAP referrals (ABAWD/JSAP) owe the
// SNAP orientation, TANF-family referrals (TANF program or EWS type) owe the
// TANF orientation, dual-family people owe both.
//
// The transition bucket decides WHETHER: per the Maximus transition plan
// (v7, loaded 08-14), B2 (active) and B3 (sanction, treated as B2) arrive
// mid-journey and SKIP orientation, as does RETENTION. B1, TRIAGE, and a
// blank bucket (every organic referral) owe it. The CRM's per-referral
// orientation_complete flag (the one thing the platform writes back
// automatically) clears the debt.
//
// Until every displayed owed video is watched to the 80% CRM threshold, the
// rest of the platform is locked (home only). That is the product rule the
// homepage cards already promised; navigation used to let people skip it.
const SKIP_BUCKETS = new Set(["B2", "B3", "RETENTION"]);

const familyOf = (referral) =>
  referral.benefit_program_code === "TANF" || referral.referral_type === "EWS"
    ? "TANF"
    : "SNAP";

// SNAP orientation = the intro-flagged/Orientation-category video; TANF has
// its own category. Case-insensitive on category like the rest of the app.
const cat = (v) => (v?.category || "").toLowerCase().trim();
export const snapOrientationVideo = (videos) =>
  (videos ?? []).find((v) => cat(v) === "orientation" && v?.url) ??
  (videos ?? []).find(
    (v) => v?.is_intro === true && cat(v) !== "tanf orientation" && v?.url,
  ) ??
  null;
export const tanfOrientationVideo = (videos) =>
  (videos ?? []).find((v) => cat(v) === "tanf orientation" && v?.url) ?? null;

// -> subset of ["SNAP", "TANF"]: the families that still owe orientation.
export const owedOrientationFamilies = (referrals) => {
  const owed = [];
  for (const family of ["SNAP", "TANF"]) {
    const fam = (referrals ?? []).filter((r) => familyOf(r) === family);
    if (fam.length === 0) continue;
    // Any referral in the family marked complete clears the whole family:
    // the person watched the video; which referral carries the flag is a
    // CRM bookkeeping detail.
    if (fam.some((r) => r.orientation_complete)) continue;
    // Owes only if at least one referral is in an orientation-owing bucket.
    const owes = fam.some(
      (r) => !SKIP_BUCKETS.has(String(r.transition_bucket ?? "").toUpperCase()),
    );
    if (owes) owed.push(family);
  }
  return owed;
};

// ---------------------------------------------------------------------------
// Catalog visibility (dashboard browse + search), as opposed to the "what do
// you still owe" rules above.
//
// A participant sees exactly ONE orientation video -- the one for their own
// program, decided by the referrals on their case:
//
//   SNAP referrals only ............ SNAP orientation  (TANF hidden)
//   TANF-family referrals only ..... TANF orientation  (SNAP hidden)
//   BOTH families .................. TANF orientation  (SNAP hidden)
//
// TANF outranks SNAP in the dual case (Marco, 09-15): a search for
// "Orientation" used to return both videos, which reads as a choice when it
// isn't -- TANF is the video that participant is told to watch.
//
// Deliberately NOT a completion rule: the CRM tracks SNAP and TANF orientation
// separately (see backend orientation.mjs), so a dual participant may still
// owe both. Home keeps listing every owed orientation via
// owedOrientationFamilies, which is where that debt gets paid; a direct ?v=
// link also still resolves, because the dashboard matches deep links against
// the unfiltered catalog.
export const orientationFamilyOf = (video) => {
  const c = cat(video);
  if (c === "tanf orientation") return "TANF";
  if (c === "orientation" || video?.is_intro === true) return "SNAP";
  return null; // not an orientation video -- never hidden by this rule
};

// -> the family whose orientation video to HIDE, or null to hide nothing.
//
// No referrals hides NOTHING, and that is load-bearing: /my-case answers with
// an EMPTY referral list (not an error) whenever it can't identify the
// participant with certainty -- most notably when one phone number matches
// several contact records, where it refuses to guess. Hiding on an empty list
// would mean withholding a required video from someone we failed to classify,
// so an unknown case view leaves the catalog exactly as it was.
export const hiddenOrientationFamily = (referrals) => {
  const list = referrals ?? [];
  if (list.length === 0) return null;
  // Any TANF-family referral (TANF program code, or an EWS referral type)
  // makes this a TANF participant for display; otherwise they are SNAP.
  return list.some((r) => familyOf(r) === "TANF") ? "SNAP" : "TANF";
};

// The catalog a participant should actually see.
export const visibleCatalog = (videos, hiddenFamily) =>
  hiddenFamily
    ? (videos ?? []).filter((v) => orientationFamilyOf(v) !== hiddenFamily)
    : (videos ?? []);

// What Home's "Start here" should offer: the families this person still owes,
// minus the one the catalog hides. Home and the dashboard then agree -- a dual
// participant is shown the TANF video in both places and never the SNAP one
// (Marco/Puneet, 09-15; the earlier build showed both here and TANF-only in
// search, which read as a bug).
//
// CONSEQUENCE, accepted with the decision: a dual participant is never offered
// the SNAP orientation anywhere, so they cannot complete it, so the legacy
// orientation_complete / orientation_date fields and the "OR - Participant
// Completes Orientation" milestone are never written for them (only SNAP
// completion writes those -- see backend orientation.mjs). TANF completion
// records the split fields only. DHW reporting that counts OR milestones will
// not see these participants until the TANF milestone question on Asana
// 1216612917062519 is settled CRM-side.
export const displayedOrientationFamilies = (referrals) => {
  const hidden = hiddenOrientationFamily(referrals);
  return owedOrientationFamilies(referrals).filter((f) => f !== hidden);
};

// Watchable orientation videos this person still has to finish. Families
// without a catalog row are omitted: we cannot require a video we cannot
// show, and a missing catalog entry must not brick the platform.
export const requiredOrientationVideos = (
  referrals,
  videos,
  watchHistory = {},
) =>
  displayedOrientationFamilies(referrals)
    .map((family) =>
      family === "TANF"
        ? tanfOrientationVideo(videos)
        : snapOrientationVideo(videos),
    )
    .filter(Boolean)
    .filter(
      (video) =>
        !isCompleted(getWatchedProgress(watchHistory[video.id]), video),
    );

// pending: case or catalog still loading -- do not bounce yet, but the
// chrome should not offer a skip path.
// locked: we know they still owe a watchable orientation video.
// needsDob: shared phone/email login awaiting DOB disambiguation -- hide
// skip paths the same way pending does; the modal is the only safe UI.
export const orientationLockState = ({
  caseStatus,
  referrals,
  videos,
  videosReady,
  watchHistory,
}) => {
  if (caseStatus === "needs_dob") {
    return {pending: false, locked: false, needsDob: true};
  }
  const pending = caseStatus === "loading" || !videosReady;
  if (pending) return {pending: true, locked: false, needsDob: false};
  const locked =
    caseStatus === "success" &&
    requiredOrientationVideos(referrals, videos, watchHistory).length > 0;
  return {pending: false, locked, needsDob: false};
};
